import React from 'react';
import { IoClose } from 'react-icons/io5';
import Modal from './Modal';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';
import tailwindTheme from '../../../../../config/tailwind.config';

// Mock Data
const mockModalContent = (
  <div className="p-8 bg-vamos-white text-center">
    <h2 className="text-2xl font-bold">Sample Modal Content</h2>
    <p className="text-lg mt-4">This is a sample content inside the modal for demonstration purposes.</p>
  </div>
);

const mockContentWithCloseButton = (
  <div className="w-122.25 min-h-20 bg-vamos-true-white rounded-md shadow-vamos-drop-shadow">
    <div className="text-center relative">
      {/* Close button */}
      <button
        type="button"
        onClick={() => {}}
        className="absolute right-3 top-5"
      >
        <IoClose size={25} color={tailwindTheme.theme.extend.colors['vamos-black']} />
      </button>
      {/* Modal body */}
      <div className="flex flex-col justify-center mt-4">
        <p className="pt-5 text-lg font-normal">Are you sure you want to perform this action?</p>
        <div className="py-8 space-x-4 text-lg">
          <button
            onClick={() => {}}
            type="button"
            className="py-1 px-3 font-medium border border-black rounded-md hover:bg-vamos-green3"
          >
            Cancel
          </button>
          <button
            onClick={() => {}}
            type="button"
            className="py-1 px-3 font-medium border border-black bg-vamos-black text-vamos-white rounded-md focus:outline-none hover:bg-vamos-green1 hover:border-vamos-green1"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Storybook metadata
export default {
  title: 'Components/Seasons/Modal',
  component: Modal,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nThe \`Modal\` component is a reusable base component for displaying modals.
        \n\n###_**Key Features:**_
        \n- Provides a full-screen overlay with centered content.
        \n- Supports dynamic content rendering through children.
        \n- Supports closing the modal by pressing the Escape key for better accessibility.\n\n`,
      // When & how to use it?
      `\n\nUse the \`Modal\` component whenever a modal window is required to display information or interact with the user.
        \n\n###_**Key Features:**_
        \n- **Dynamic Content**:
        \n  - Accepts children as the content to be displayed within the modal.
        \n\n- **Overlay Behavior**:
        \n  - Full-screen overlay with click-outside-to-close functionality.
        \n\n- **Escape Key Support**:
        \n  - The modal can be closed by pressing the **Escape** key, ensuring easy dismissal and accessibility.\n\n`,
      // Current Usage
      '\nThe `Modal` component is used as a base component in the Seasons feature for displaying modals like the export modal, new season modal and confirm delete modal.\n\n',
      // Related Components
      `\n- **[ExportSeasonsModal](http://localhost:6006/?path=/docs/components-seasons-exportseasonsmodal--docs)**: Extends this base component for export functionality.
       \n- **[NewSeasonModal](http://localhost:6006/?path=/docs/components-seasons-newseasonmodal--docs)**: Modal for adding a new season, including input fields for name and date range.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    isShowing: {
      control: { type: 'boolean' },
      description: 'Controls the visibility of the modal.',
      table: {
        type: { summary: 'boolean' },
        required: true,
      },
    },
    onHide: {
      control: { type: 'function' },
      description: 'Callback function to hide the modal.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    children: {
      control: { type: 'node' },
      description: 'Content to be displayed inside the modal.',
      table: {
        type: { summary: 'node' },
        required: true,
      },
    },
  },
};

// Template for the stories
const Template = (args) => (
  <div style={{
    height: '50vh', width: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}
  >
    <Modal {...args} />
  </div>
);

// Default Modal Story
export const DefaultModal = Template.bind({});
DefaultModal.args = {
  isShowing: true,
  onHide: () => {},
  children: mockModalContent,
};
DefaultModal.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display a modal with default content.\n\n',
  // Where is it currently used on the product?
  '\nThe `Modal` component is used as a base component in various features for displaying overlay content.\n\n',
);

// Modal with Close Button Story
export const ModalWithCloseButton = Template.bind({});
ModalWithCloseButton.args = {
  isShowing: true,
  onHide: () => {},
  children: mockContentWithCloseButton,
};

ModalWithCloseButton.parameters = generateStoryDocs(
  '\nUse this variant to display a modal with a close button and confirmation actions.\n\n',
  '\nThis variant is used for modals requiring user confirmation or actions, such as deletion or export operations.\n\n',
);
